"use client";
/**
 * StageApprovalBanner · 2026-10-12
 *   顯示 4 階段審核鏈中「等我處理」的專案數量 + 跳審核頁入口
 *   每階段一條 banner,全空時整區不顯示
 */
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

interface Item { id: number; name: string }

const STAGE_META: Record<"leaderFill" | "pm" | "director" | "finance", {
  icon: string;
  title: string;
  labelName: string;
  bg: string;
  border: string;
  color: string;
  btnBg: string;
  linkPath: (id: number) => string;
  cta: string;
}> = {
  leaderFill: {
    icon: "📝", title: "你是負責人 · 待填料", labelName: "填料",
    bg: "#FFFBEB", border: "#FCD34D", color: "#78350F", btnBg: "#F59E0B",
    linkPath: (id) => `/projects/${id}`,
    cta: "去填 →",
  },
  pm: {
    icon: "🧑‍💼", title: "等 PM 審報價", labelName: "PM 審",
    bg: "#EEF2FF", border: "#C7D2FE", color: "#3730A3", btnBg: "#4F46E5",
    linkPath: (id) => `/projects/${id}/approve`,
    cta: "進審核 →",
  },
  director: {
    icon: "👔", title: "等總監審報價", labelName: "總監審",
    bg: "#F5F3FF", border: "#DDD6FE", color: "#5B21B6", btnBg: "#7C3AED",
    linkPath: (id) => `/projects/${id}/approve`,
    cta: "進審核 →",
  },
  finance: {
    icon: "💰", title: "等財務簽收(最終)", labelName: "財務簽收",
    bg: "#D1FAE5", border: "#6EE7B7", color: "#065F46", btnBg: "#10B981",
    linkPath: (id) => `/projects/${id}/approve`,
    cta: "進審核 →",
  },
};

interface State {
  leaderFill: Item[]; pm: Item[]; director: Item[]; finance: Item[];
}

export default function StageApprovalBanner() {
  const [state, setState] = useState<State>({ leaderFill: [], pm: [], director: [], finance: [] });
  const pathname = usePathname();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await fetch("/api/my/stage-pending", { credentials: "include", cache: "no-store" });
        if (!r.ok) return;
        const j = await r.json();
        if (mounted) setState({
          leaderFill: j.leaderFill ?? [],
          pm: j.pm ?? [],
          director: j.director ?? [],
          finance: j.finance ?? [],
        });
      } catch { /* noop */ }
    };
    load();
    const h = setInterval(load, 60_000);
    return () => { mounted = false; clearInterval(h); };
  }, [pathname]);

  const rows = (["leaderFill", "pm", "director", "finance"] as const)
    .map((k) => ({ k, items: state[k] }))
    .filter((r) => r.items.length > 0);

  if (rows.length === 0) return null;

  return (
    <div style={{ display: "grid", gap: 6, marginBottom: 10 }}>
      {rows.map(({ k, items }) => {
        const m = STAGE_META[k];
        return (
          <div key={k} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "8px 14px", background: m.bg, border: `1px solid ${m.border}`,
            borderRadius: 6, fontSize: 13, color: m.color, flexWrap: "wrap",
          }}>
            <span style={{ fontSize: 16 }}>{m.icon}</span>
            <span style={{ fontWeight: 500 }}>
              {m.title} · <b>{items.length}</b> 案
            </span>
            <span style={{ flex: 1, minWidth: 100, fontSize: 11, opacity: 0.8 }}>
              {items.slice(0, 3).map((it) => `#${it.id} ${it.name}`).join("、")}
              {items.length > 3 ? ` 等 ${items.length} 案` : ""}
            </span>
            {items.length === 1 ? (
              <a href={m.linkPath(items[0].id)} style={{
                background: m.btnBg, color: "#fff", padding: "4px 12px",
                borderRadius: 4, fontSize: 12, fontWeight: 600, textDecoration: "none",
              }}>{m.cta}</a>
            ) : (
              <span style={{ fontSize: 11, opacity: 0.7 }}>點清單進每案</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
